/**
 * Report a hazard or incident from the phone (ADR-0029). Works offline: the
 * site list comes from the offline pack, and the report is queued as one
 * idempotent `event.report` op that is sent when there is signal.
 * Pre-filled: reporter (the signed-in user), date/time (now), GPS and, when
 * the reporter has one site or opened the form from a site, the site.
 * Classification is tap-only; the narrative can be dictated.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { validateSubmission, type SurveyContext } from '@sss/form-engine';
import {
  CONFLICT_TYPE,
  CONSEQUENCES,
  EVENT_SEVERITIES,
  EventReportInput,
  INCIDENT_SUBCATEGORIES,
  INCIDENT_TYPES,
  LIKELIHOODS,
  MAX_EVENT_PHOTOS,
  OFFICER_RESPONSES,
  SUBJECT_ACTIONS,
  WEAPONS,
  type ConflictStep,
  type Consequence,
  type EventKind,
  type EventSeverity,
  type IncidentType,
  type Likelihood,
  type Weapon,
} from '@sss/events';
import { Banner, Button, Card, Chips, EmptyState, Field, Icon, IconButton, Input, Segmented, Select, Textarea, useToast } from '../../components';
import { PageHeader } from '../../app/AppShell';
import { useOnline } from '../../app/hooks';
import { isNative } from '../../lib/device';
import { compressMedia } from '../../lib/media/compress';
import { useServices } from '../../app/services';
import { useI18n } from '../../lib/i18n';
import { positionOrNull, takeNativePhoto, type Position } from '../../lib/native';
import { useNavigate, useSearchParams } from '../../lib/router';
import type { PackSite } from '../../lib/api/types';
import { VoiceNoteButton } from '../capture/VoiceNoteButton';
import { queueReport, type ReportPhoto } from './event-outbox';
import { EventQuestions, visibleEventFields } from './EventQuestions';
import { useEventSites } from './useEventSites';
import { consequenceKey, likelihoodKey, responseKey, severityKey, subjectKey, subKey, typeKey, weaponKey } from './labels';

/** "YYYY-MM-DDTHH:mm" in local time, for <input type="datetime-local">. */
function localNow(d = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

function distanceKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const r = (x: number) => (x * Math.PI) / 180;
  const dLat = r(b.lat - a.lat);
  const dLng = r(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 12_742 * Math.asin(Math.sqrt(h));
}

/** Nearest site first when we know where the reporter is, else by name. */
export function orderSites(sites: PackSite[], pos: Position | null): PackSite[] {
  const active = sites.filter((s) => s.status !== 'INACTIVE');
  if (!pos) return [...active].sort((a, b) => a.name.localeCompare(b.name));
  const d = (s: PackSite) => (s.lat != null && s.lng != null ? distanceKm(pos, { lat: s.lat, lng: s.lng }) : Number.POSITIVE_INFINITY);
  return [...active].sort((a, b) => d(a) - d(b) || a.name.localeCompare(b.name));
}

type Missing = 'site' | 'occurredAt' | 'incidentType' | 'subCategory' | 'severity' | 'injuries' | 'conflict' | 'description' | 'questions';

export function ReportEventPage() {
  const { t } = useI18n();
  const { sync, storage } = useServices();
  const toast = useToast();
  const navigate = useNavigate();
  const online = useOnline();
  const [params] = useSearchParams();
  const siteList = useEventSites();
  const [reportId] = useState(() => crypto.randomUUID());

  const [kind, setKind] = useState<EventKind>(params.get('kind') === 'HAZARD' ? 'HAZARD' : 'INCIDENT');
  const [siteId, setSiteId] = useState<string>(params.get('site') ?? '');
  const [occurredAt, setOccurredAt] = useState(localNow());
  const [incidentType, setIncidentType] = useState<IncidentType | null>(null);
  const [subCategory, setSubCategory] = useState<string | null>(null);
  const [severity, setSeverity] = useState<EventSeverity | null>(null);
  const [injuries, setInjuries] = useState<'YES' | 'NO' | null>(null);
  const [steps, setSteps] = useState<Partial<ConflictStep>[]>([{}]);
  const [weapons, setWeapons] = useState<Weapon[]>([]);
  const [dog, setDog] = useState<'YES' | 'NO'>('NO');
  const [consequence, setConsequence] = useState<Consequence | ''>('');
  const [likelihood, setLikelihood] = useState<Likelihood | ''>('');
  const [description, setDescription] = useState('');
  const [locationNote, setLocationNote] = useState('');
  const [immediateAction, setImmediateAction] = useState('');
  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [photos, setPhotos] = useState<(ReportPhoto & { url: string })[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const [pos, setPos] = useState<Position | null>(null);
  const [tried, setTried] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let live = true;
    void positionOrNull(8_000).then((p) => live && setPos(p));
    return () => {
      live = false;
    };
  }, []);

  const ordered = useMemo(() => orderSites((siteList.data?.sites ?? []) as PackSite[], pos), [siteList.data, pos]);
  // One site, or the nearest when we have a fix: pre-select it (the reporter can change it).
  useEffect(() => {
    if (!siteId && ordered.length > 0 && (ordered.length === 1 || pos)) setSiteId(ordered[0]!.id);
  }, [ordered, pos, siteId]);

  const isConflict = kind === 'INCIDENT' && !!incidentType && CONFLICT_TYPE.has(incidentType);

  // The country's extra questions for this kind of report at this site, if it has any.
  const site = siteList.data?.sites.find((s) => s.id === siteId);
  const templateVersionId = site?.templates?.[kind];
  const template = templateVersionId ? siteList.data?.templates[templateVersionId] : undefined;
  const context: SurveyContext = { site: { region: site?.regionCode ?? null, country: site?.countryCode ?? null, serviceLine: site?.serviceLine ?? null, branch: site?.branchCode ?? null } };
  const shownKeys = new Set(template ? visibleEventFields(template, answers, context).map((f) => f.key) : []);
  const questionIssues = template ? validateSubmission(template, { fields: answers, controls: {} }, context).filter((i) => shownKeys.has(i.ref)) : [];

  async function addPhoto(blob: Blob | null) {
    if (!blob) return;
    const small = await compressMedia(blob).catch(() => blob);
    setPhotos((cur) => [...cur, { id: crypto.randomUUID(), blob: small, takenAt: new Date().toISOString(), lat: pos?.lat, lng: pos?.lng, url: URL.createObjectURL(small) }]);
  }
  useEffect(() => () => photos.forEach((p) => URL.revokeObjectURL(p.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  function build(): EventReportInput | Missing[] {
    const missing: Missing[] = [];
    if (!siteId) missing.push('site');
    const when = new Date(occurredAt);
    if (!occurredAt || Number.isNaN(when.getTime())) missing.push('occurredAt');
    if (!description.trim()) missing.push('description');
    if (questionIssues.length) missing.push('questions');
    const shownAnswers = Object.fromEntries(Object.entries(answers).filter(([k, v]) => shownKeys.has(k) && v !== undefined));
    const common = {
      id: reportId,
      ...(template && templateVersionId ? { templateVersionId, answers: shownAnswers } : {}),
      siteId,
      occurredAt: Number.isNaN(when.getTime()) ? '' : when.toISOString(),
      description,
      ...(locationNote.trim() ? { locationNote } : {}),
      ...(immediateAction.trim() ? { immediateAction } : {}),
      ...(pos ? { gps: { lat: pos.lat, lng: pos.lng, accuracy: pos.accuracy } } : {}),
    };
    let candidate: unknown;
    if (kind === 'HAZARD') {
      candidate = { kind, ...common, ...(consequence ? { consequence } : {}), ...(likelihood ? { likelihood } : {}) };
    } else {
      if (!incidentType) missing.push('incidentType');
      if (!subCategory) missing.push('subCategory');
      if (!severity) missing.push('severity');
      if (!injuries) missing.push('injuries');
      const complete = steps.filter((s): s is ConflictStep => !!s.subject && !!s.response);
      if (isConflict && (complete.length === 0 || complete.length !== steps.length)) missing.push('conflict');
      candidate = {
        kind,
        ...common,
        incidentType,
        subCategory,
        severity,
        injuries: injuries === 'YES',
        ...(isConflict
          ? {
              conflict: {
                steps: complete,
                weaponsInvolved: weapons,
                dogDeployed: dog === 'YES',
                ...(consequence ? { consequence } : {}),
                ...(likelihood ? { likelihood } : {}),
              },
            }
          : {}),
      };
    }
    if (missing.length) return missing;
    const parsed = EventReportInput.safeParse(candidate);
    if (parsed.success) return parsed.data;
    // Anything the checks above didn't catch: point at the field the schema names.
    const FIELDS: Missing[] = ['site', 'occurredAt', 'incidentType', 'subCategory', 'severity', 'injuries', 'conflict', 'description'];
    const named = parsed.error.issues.map((i) => (i.path[0] === 'siteId' ? 'site' : String(i.path[0]))).filter((f): f is Missing => FIELDS.includes(f as Missing));
    return named.length ? named : ['description'];
  }

  const result = build();
  const missing = Array.isArray(result) ? result : [];
  const err = (m: Missing) => (tried && missing.includes(m) ? t('ui.events.form.required') : undefined);

  async function send() {
    setTried(true);
    const r = build();
    if (Array.isArray(r)) {
      toast.show(t('ui.events.form.fixErrors'), 'warning');
      return;
    }
    setSending(true);
    try {
      await queueReport(sync, storage, r, photos);
      toast.show(online ? t('ui.events.form.sent') : t('ui.events.form.queued'), 'success');
      navigate('/events?mine=true');
    } catch {
      // Nothing was queued half-way that matters: the report op is queued last.
      toast.show(t('ui.events.form.saveFailed'), 'danger');
    } finally {
      setSending(false);
    }
  }

  if (siteList.data && siteList.data.sites.length === 0) {
    return (
      <>
        <PageHeader title={t('ui.events.form.title')} back="/" />
        <EmptyState icon="mapPin" title={t('ui.events.form.noSites')} description={t('ui.events.form.noSitesHelp')} />
      </>
    );
  }

  const yesNo = [
    { value: 'NO' as const, label: t('ui.events.form.no') },
    { value: 'YES' as const, label: t('ui.events.form.yes') },
  ];

  return (
    <>
      <PageHeader title={t('ui.events.form.title')} subtitle={t('ui.events.form.subtitle')} back="/" />
      <div className="mx-auto flex max-w-2xl flex-col gap-5 pb-24">
        {!online && <Banner tone="info">{t('ui.events.form.offline')}</Banner>}

        <Segmented<EventKind>
          label={t('ui.events.form.kind')}
          value={kind}
          onChange={(v) => v && setKind(v)}
          options={[
            { value: 'INCIDENT', label: t('ui.events.kind.INCIDENT') },
            { value: 'HAZARD', label: t('ui.events.kind.HAZARD'), tone: 'warning' },
          ]}
        />
        <p className="-mt-3 text-sm text-muted">{t(kind === 'HAZARD' ? 'ui.events.form.hazardHelp' : 'ui.events.form.incidentHelp')}</p>

        <Card className="flex flex-col gap-4 p-4">
          <Field label={t('ui.events.form.site')} required error={err('site')}>
            {(id) => (
              <Select id={id} value={siteId} onChange={(e) => setSiteId(e.target.value)} invalid={!!err('site')}>
                <option value="">{t('ui.events.form.chooseSite')}</option>
                {ordered.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.customerName ? ` · ${s.customerName}` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('ui.events.form.occurredAt')} required error={err('occurredAt')}>
            {(id) => <Input id={id} type="datetime-local" value={occurredAt} max={localNow()} onChange={(e) => setOccurredAt(e.target.value)} />}
          </Field>
          <p className="flex items-center gap-1.5 text-xs text-muted">
            <Icon name="navigation" size={14} />
            {pos ? t('ui.events.form.gpsAttached', { accuracy: Math.round(pos.accuracy) }) : t('ui.events.form.gpsNone')}
          </p>
        </Card>

        {kind === 'INCIDENT' && (
          <Card className="flex flex-col gap-4 p-4">
            <Field label={t('ui.events.form.type')} required error={err('incidentType')}>
              {() => (
                <Chips<IncidentType>
                  options={INCIDENT_TYPES.map((v) => ({ value: v, label: t(typeKey(v)) }))}
                  selected={incidentType ? [incidentType] : []}
                  onToggle={(v) => {
                    setIncidentType(v);
                    setSubCategory(null);
                  }}
                />
              )}
            </Field>
            {incidentType && (
              <Field label={t('ui.events.form.subCategory')} required error={err('subCategory')}>
                {() => (
                  <Chips<string>
                    options={INCIDENT_SUBCATEGORIES[incidentType].map((v) => ({ value: v, label: t(subKey(v)) }))}
                    selected={subCategory ? [subCategory] : []}
                    onToggle={setSubCategory}
                  />
                )}
              </Field>
            )}
            <Field label={t('ui.events.form.severity')} required error={err('severity')}>
              {() => (
                <Chips<EventSeverity>
                  options={[...EVENT_SEVERITIES].reverse().map((v) => ({ value: v, label: t(severityKey(v)) }))}
                  selected={severity ? [severity] : []}
                  onToggle={setSeverity}
                />
              )}
            </Field>
            <Field label={t('ui.events.form.injuries')} required error={err('injuries')}>
              {() => <Segmented label={t('ui.events.form.injuries')} size="md" value={injuries} onChange={(v) => v && setInjuries(v)} options={yesNo} />}
            </Field>
          </Card>
        )}

        {isConflict && (
          <Card className="flex flex-col gap-4 p-4">
            <div>
              <h2 className="font-display text-base font-bold">{t('ui.events.form.conflictTitle')}</h2>
              <p className="text-sm text-muted">{t('ui.events.form.conflictHelp')}</p>
            </div>
            {err('conflict') && <p className="text-sm font-medium text-danger">{t('ui.events.form.stepsRequired')}</p>}
            {steps.map((s, i) => (
              <div key={i} className="flex flex-col gap-3 rounded-xl border border-border p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">{t('ui.events.form.step', { n: i + 1 })}</span>
                  {steps.length > 1 && (
                    <IconButton icon="trash" label={t('ui.events.form.removeStep')} size="sm" onClick={() => setSteps(steps.filter((_, j) => j !== i))} />
                  )}
                </div>
                <Field label={t('ui.events.form.subjectAction')}>
                  {(id) => (
                    <Select id={id} value={s.subject ?? ''} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, subject: (e.target.value || undefined) as ConflictStep['subject'] } : x)))}>
                      <option value="">{t('ui.events.form.choose')}</option>
                      {SUBJECT_ACTIONS.map((v, n) => (
                        <option key={v} value={v}>{`${n + 1} · ${t(subjectKey(v))}`}</option>
                      ))}
                    </Select>
                  )}
                </Field>
                <Field label={t('ui.events.form.officerResponse')}>
                  {(id) => (
                    <Select id={id} value={s.response ?? ''} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, response: (e.target.value || undefined) as ConflictStep['response'] } : x)))}>
                      <option value="">{t('ui.events.form.choose')}</option>
                      {OFFICER_RESPONSES.map((v, n) => (
                        <option key={v} value={v}>{`${n + 1} · ${t(responseKey(v))}`}</option>
                      ))}
                    </Select>
                  )}
                </Field>
              </div>
            ))}
            {steps.length < 20 && (
              <Button variant="secondary" icon="plus" onClick={() => setSteps([...steps, {}])}>
                {t('ui.events.form.addStep')}
              </Button>
            )}
            <Field label={t('ui.events.form.weapons')}>
              {() => (
                <Chips<Weapon>
                  options={WEAPONS.map((v) => ({ value: v, label: t(weaponKey(v)) }))}
                  selected={weapons}
                  onToggle={(v) => setWeapons(weapons.includes(v) ? weapons.filter((w) => w !== v) : [...weapons, v])}
                />
              )}
            </Field>
            <Field label={t('ui.events.form.dog')}>
              {() => <Segmented label={t('ui.events.form.dog')} size="md" value={dog} onChange={(v) => v && setDog(v)} options={yesNo} />}
            </Field>
          </Card>
        )}

        {(kind === 'HAZARD' || isConflict) && (
          <Card className="grid gap-4 p-4 sm:grid-cols-2">
            <Field label={t('ui.events.form.consequence')} help={t('ui.events.form.optional')}>
              {(id) => (
                <Select id={id} value={consequence} onChange={(e) => setConsequence(e.target.value as Consequence | '')}>
                  <option value="">{t('ui.events.form.choose')}</option>
                  {CONSEQUENCES.map((v) => (
                    <option key={v} value={v}>
                      {t(consequenceKey(v))}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t('ui.events.form.likelihood')} help={t('ui.events.form.optional')}>
              {(id) => (
                <Select id={id} value={likelihood} onChange={(e) => setLikelihood(e.target.value as Likelihood | '')}>
                  <option value="">{t('ui.events.form.choose')}</option>
                  {LIKELIHOODS.map((v) => (
                    <option key={v} value={v}>
                      {t(likelihoodKey(v))}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </Card>
        )}

        <Card className="flex flex-col gap-4 p-4">
          <Field
            label={t('ui.events.form.description')}
            help={t(isConflict ? 'ui.events.form.descriptionHelpConflict' : 'ui.events.form.descriptionHelp')}
            required
            error={err('description')}
            labelAction={<VoiceNoteButton currentNote={description} onAppend={setDescription} />}
          >
            {(id) => <Textarea id={id} rows={5} value={description} maxLength={8000} onChange={(e) => setDescription(e.target.value)} invalid={!!err('description')} />}
          </Field>
          <Field label={t('ui.events.form.locationNote')} help={t('ui.events.form.optional')}>
            {(id) => <Input id={id} value={locationNote} maxLength={500} onChange={(e) => setLocationNote(e.target.value)} />}
          </Field>
          <Field label={t('ui.events.form.immediateAction')} help={t('ui.events.form.optional')}>
            {(id) => <Textarea id={id} rows={3} value={immediateAction} maxLength={4000} onChange={(e) => setImmediateAction(e.target.value)} />}
          </Field>
        </Card>

        {template && shownKeys.size > 0 && (
          <Card className="flex flex-col gap-4 p-4">
            <h2 className="font-display text-base font-bold">{t('ui.events.form.moreQuestions')}</h2>
            <EventQuestions template={template} answers={answers} onChange={setAnswers} context={context} issues={tried ? questionIssues : []} />
          </Card>
        )}

        <Card className="flex flex-col gap-3 p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="font-display text-base font-bold">{t('ui.events.form.photos')}</h2>
            {photos.length < MAX_EVENT_PHOTOS && (
              <Button
                size="sm"
                variant="secondary"
                icon="camera"
                onClick={async () => {
                  if (isNative()) await addPhoto(await takeNativePhoto().catch(() => null));
                  else fileInput.current?.click();
                }}
              >
                {t('ui.events.form.addPhoto')}
              </Button>
            )}
          </div>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            aria-label={t('ui.events.form.addPhoto')}
            onChange={async (e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              await addPhoto(f ?? null);
            }}
          />
          {photos.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {photos.map((p, i) => (
                <div key={p.id} className="relative">
                  <img src={p.url} alt={t('ui.events.form.photoN', { n: i + 1 })} className="size-24 rounded-lg object-cover" />
                  <IconButton
                    icon="x"
                    size="sm"
                    variant="secondary"
                    label={t('ui.events.form.removePhoto')}
                    className="absolute -end-2 -top-2"
                    onClick={() => {
                      URL.revokeObjectURL(p.url);
                      setPhotos(photos.filter((x) => x.id !== p.id));
                    }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted">{t('ui.events.form.photosHelp')}</p>
          )}
        </Card>

        <Button block size="lg" icon="send" onClick={send} disabled={sending}>
          {t('ui.events.form.send')}
        </Button>
      </div>
    </>
  );
}
